// ============================================================================
// lazyRetry — import dinámico resistente a deploys
// ----------------------------------------------------------------------------
// Cada deploy publica chunks con hash nuevo. Una pestaña abierta antes del
// deploy que navega a un módulo aún no descargado pide un chunk que puede no
// existir ya (ChunkLoadError). Este helper:
//   1. Reintenta el import hasta RETRIES veces con una espera corta (cubre
//      fallos de red puntuales; webpack 5 limpia el chunk fallido y lo vuelve
//      a pedir en el reintento).
//   2. Si sigue fallando por carga de chunk y no se ha recargado ya por esta
//      clave en esta sesión, recarga la página UNA vez (flag en sessionStorage)
//      para traer el index.html nuevo con los hashes vigentes.
//   3. Si ya se recargó (o el error no es de carga de chunk), lanza el error
//      para que lo capture el ModuleErrorBoundary del módulo.
// Uso: lazy(() => lazyRetry(() => import('./X'), 'X'))
// ============================================================================

const RETRIES = 2;
const RETRY_DELAY_MS = 400;
const FLAG_PREFIX = 'tm_chunk_reload_';

// Errores de carga de chunk JS (webpack: name ChunkLoadError, "Loading chunk N
// failed") y de CSS (mini-css-extract: "Loading CSS chunk N failed").
export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError' || error.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  return /Loading (CSS )?chunk [^ ]+ failed/i.test(String(error.message || ''));
}

// sessionStorage puede lanzar (modo privado, cookies bloqueadas): nunca debe
// romper la carga del módulo.
function readFlag(key) {
  try { return window.sessionStorage.getItem(FLAG_PREFIX + key) === '1'; } catch (e) { return false; }
}
function writeFlag(key) {
  try { window.sessionStorage.setItem(FLAG_PREFIX + key, '1'); return true; } catch (e) { return false; }
}
function clearFlag(key) {
  try { window.sessionStorage.removeItem(FLAG_PREFIX + key); } catch (e) { /* sin storage */ }
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default async function lazyRetry(importFn, key) {
  let lastError;
  for (let attempt = 0; attempt <= RETRIES; attempt += 1) {
    try {
      const mod = await importFn();
      // Carga buena: se limpia el flag para que un deploy posterior en esta
      // misma sesión pueda volver a recargar una vez.
      clearFlag(key);
      return mod;
    } catch (error) {
      lastError = error;
      if (attempt < RETRIES) await wait(RETRY_DELAY_MS * (attempt + 1));
    }
  }

  // Solo merece la pena recargar si falta el chunk (build nuevo publicado).
  // Sin storage no hay forma de evitar un bucle, así que no se recarga.
  if (isChunkLoadError(lastError) && !readFlag(key) && writeFlag(key)) {
    window.location.reload();
    // Promesa que no resuelve: evita pintar el error mientras la página recarga.
    return new Promise(() => {});
  }

  throw lastError;
}
